// src/utils/annotationPdf.ts — SCE Issues
// Annoter un PDF (12/10) : les formes dessinées sur les pages dans l'éditeur
// (flèche, cercle, trait, texte — utils/annotation) sont inscrites dans une
// COPIE du PDF, en VECTORIEL : un plan annoté reste un PDF net, qui se zoome,
// s'imprime et garde son texte sélectionnable. L'original n'est jamais modifié.
//
// Repères : l'éditeur dessine sur l'image d'une page rendue par pdf.js, en
// pixels de cette image. Chaque page annotée porte la matrice qui ramène ces
// pixels dans l'espace du PDF (l'inverse de `viewport.transform` de pdf.js) :
// rotation de page (/Rotate), cadre décalé (CropBox) et axe des y retourné y
// sont déjà. Une forme pivotée est remise à plat ici, en points.
//
// pdf-lib est chargé à la demande (comme pour le rapport FNC-T).

import type { Forme, Point } from '@/utils/annotation'
import { centre, tourner } from '@/utils/annotation'

/** Affine 2D au format PDF / pdf.js : [a, b, c, d, e, f] → (a·x + c·y + e, b·x + d·y + f). */
export type Matrice = [number, number, number, number, number, number]

export interface PageAnnotee {
  /** Rang de la page, à partir de 0. */
  index: number
  formes: Forme[]
  /** Pixels de l'image de la page → espace du PDF. */
  matrice: Matrice
  /** Épaisseur des tracés, en pixels de l'image (celle de l'éditeur). */
  epaisseur: number
}

export function appliquer(m: Matrice, p: Point): Point {
  return { x: m[0] * p.x + m[2] * p.y + m[4], y: m[1] * p.x + m[3] * p.y + m[5] }
}

export function inverser(m: Matrice): Matrice {
  const [a, b, c, d, e, f] = m
  const det = a * d - b * c
  if (!det) throw new Error('Matrice de page dégénérée')
  return [d / det, -b / det, -c / det, a / det, (c * f - d * e) / det, (b * e - a * f) / det]
}

/** Facteur d'échelle de la matrice (pixels → points), rotation et retournement mis de côté. */
export function echelle(m: Matrice): number {
  return Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]))
}

/** « #0A6CFF » → composantes de 0 à 1. */
export function composantes(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  const plein = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  const n = Number.parseInt(plein, 16)
  if (Number.isNaN(n) || plein.length !== 6) return [0, 0, 0]
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

/** Points par tour pour approcher une ellipse : assez pour rester ronde sur un A0. */
const POINTS_ELLIPSE = 72
/** Ouverture de la pointe d'une flèche, comme dans l'éditeur. */
const OUVERTURE_POINTE = 0.5

/**
 * Les tracés d'une forme (hors texte), en pixels de l'image, orientation
 * comprise : chaque liste de points est un trait continu. Même géométrie que
 * le dessin de l'éditeur (pointe de flèche à 6 épaisseurs).
 */
export function traces(f: Forme, epaisseur: number): Point[][] {
  if (f.outil === 'texte') return []
  const c = centre(f)
  const angle = f.angle ?? 0
  const t = (p: Point): Point => tourner(p, c, angle)
  const [a, b] = [f.points[0]!, f.points[f.points.length - 1]!]

  if (f.outil === 'trait') return f.points.length > 1 ? [f.points.map(t)] : []
  if (f.outil === 'cercle') {
    const cx = (a.x + b.x) / 2
    const cy = (a.y + b.y) / 2
    const rx = Math.abs(b.x - a.x) / 2
    const ry = Math.abs(b.y - a.y) / 2
    const tour: Point[] = []
    for (let i = 0; i <= POINTS_ELLIPSE; i++) {
      const th = (i / POINTS_ELLIPSE) * Math.PI * 2
      tour.push(t({ x: cx + rx * Math.cos(th), y: cy + ry * Math.sin(th) }))
    }
    return [tour]
  }
  // Flèche : la hampe, puis la pointe d'un seul trait (bout → sommet → bout).
  const dir = Math.atan2(b.y - a.y, b.x - a.x)
  const pointe = epaisseur * 6
  return [
    [t(a), t(b)],
    [
      t({ x: b.x - pointe * Math.cos(dir - OUVERTURE_POINTE), y: b.y - pointe * Math.sin(dir - OUVERTURE_POINTE) }),
      t(b),
      t({ x: b.x - pointe * Math.cos(dir + OUVERTURE_POINTE), y: b.y - pointe * Math.sin(dir + OUVERTURE_POINTE) }),
    ],
  ]
}

/**
 * Où et comment écrire un texte dans le PDF : pied de la première lettre,
 * taille et angle en points. L'éditeur pose le texte par son MILIEU (canvas,
 * `textBaseline: middle`) ; la ligne de base est 0,35 taille plus bas.
 */
export function placementTexte(
  f: Extract<Forme, { outil: 'texte' }>,
  largeurTexte: number,
  m: Matrice,
): { x: number; y: number; taille: number; angle: number } {
  const c = centre(f, largeurTexte)
  const angle = f.angle ?? 0
  const a = f.points[0]
  const pied = tourner({ x: a.x, y: a.y + f.taille * 0.35 }, c, angle)
  const droite = tourner({ x: a.x + 1, y: a.y + f.taille * 0.35 }, c, angle)
  const p = appliquer(m, pied)
  const q = appliquer(m, droite)
  return { x: p.x, y: p.y, taille: f.taille * echelle(m), angle: Math.atan2(q.y - p.y, q.x - p.x) }
}

/**
 * Helvetica (repli sans TT Norms Pro) ne connaît que l'alphabet latin d'Europe
 * de l'Ouest : une flèche « → » ou un émoji y ferait échouer TOUT l'export.
 * Ils deviennent « ? » ; accents, œ, €, apostrophe typographique passent.
 */
export function texteHelvetica(texte: string): string {
  return texte.replace(/[^ -~ -ÿŒœ€‘’“”–—…]/gu, '?')
}

/** Le PDF ne peut pas être modifié (protégé par mot de passe ou restrictions). */
export class PdfProtegeError extends Error {
  constructor() {
    super('Ce PDF est protégé : il ne peut pas être annoté.')
    this.name = 'PdfProtegeError'
  }
}

/**
 * Copie du PDF avec les formes inscrites en vectoriel. `police` : TT Norms Pro
 * (TTF) pour les textes ; sans elle, Helvetica (accents français compris).
 */
export async function annoterPdf(
  original: Uint8Array,
  pages: readonly PageAnnotee[],
  police?: ArrayBuffer | Uint8Array,
): Promise<Uint8Array> {
  const lib = await import('pdf-lib')
  const { PDFDocument, StandardFonts, rgb, radians, LineCapStyle, LineJoinStyle } = lib
  let doc: Awaited<ReturnType<typeof PDFDocument.load>>
  try {
    // Les métadonnées d'origine (titre, auteur) restent celles du document.
    doc = await PDFDocument.load(original, { updateMetadata: false })
  } catch (e) {
    if (e instanceof Error && /encrypt/i.test(e.message)) throw new PdfProtegeError()
    throw e
  }

  const avecTexte = pages.some((p) => p.formes.some((f) => f.outil === 'texte'))
  let fonte: Awaited<ReturnType<typeof doc.embedFont>> | null = null
  if (avecTexte) {
    if (police) {
      const { default: fontkit } = await import('@pdf-lib/fontkit')
      doc.registerFontkit(fontkit)
      fonte = await doc.embedFont(police, { subset: true })
    } else {
      fonte = await doc.embedFont(StandardFonts.Helvetica)
    }
  }

  const nbPages = doc.getPageCount()
  for (const p of pages) {
    if (p.index < 0 || p.index >= nbPages || p.formes.length === 0) continue
    const page = doc.getPage(p.index)
    const k = echelle(p.matrice)
    for (const f of p.formes) {
      const [r, g, b] = composantes(f.couleur)
      if (f.outil === 'texte') {
        if (!fonte || !f.texte.trim()) continue
        const texte = police ? f.texte : texteHelvetica(f.texte)
        const largeur = fonte.widthOfTextAtSize(texte, f.taille)
        const t = placementTexte(f, largeur, p.matrice)
        page.drawText(texte, { x: t.x, y: t.y, size: t.taille, font: fonte, color: rgb(r, g, b), rotate: radians(t.angle) })
        continue
      }
      const ops = [
        lib.pushGraphicsState(),
        lib.setStrokingColor(rgb(r, g, b)),
        lib.setLineWidth(p.epaisseur * k),
        lib.setLineCap(LineCapStyle.Round),
        lib.setLineJoin(LineJoinStyle.Round),
      ]
      for (const trace of traces(f, p.epaisseur)) {
        const [premier, ...suite] = trace.map((q) => appliquer(p.matrice, q))
        if (!premier || suite.length === 0) continue
        ops.push(lib.moveTo(premier.x, premier.y))
        for (const q of suite) ops.push(lib.lineTo(q.x, q.y))
        ops.push(lib.stroke())
      }
      ops.push(lib.popGraphicsState())
      page.pushOperators(...ops)
    }
  }
  return doc.save()
}
